// TemplateManager.js

import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { Image as ImageIcon, Video, Type, Trash2, RefreshCw, PlusCircle } from 'lucide-react';
import '../styles/TemplateManager.css';

const TemplateManager = ({ estabelecimentoId }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Estados do Formulário (Controlam o Preview e a Edição)
    const [templateId, setTemplateId] = useState(null);
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [tipoArquivo, setTipoArquivo] = useState('txt');
    const [file, setFile] = useState(null);
    const [urlVideo, setUrlVideo] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');

    const variaveis = ['@NomeContato', '@TelefoneContato', '@v1', '@v2', '@v3'];

    useEffect(() => {
        buscarTemplates();
    }, [estabelecimentoId]);

    const buscarTemplates = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/whatsapp/templates/${estabelecimentoId}`);
            setTemplates(res.data.payload || []);
        } catch (err) {
            toast.error("Erro ao carregar templates.");
        } finally {
            setLoading(false);
        }
    };

    // 🔥 Função auxiliar corrigida para garantir que a URL da imagem aponte para o servidor correto
    const getImageUrl = (caminho) => {
        if (!caminho) return '';
        if (caminho.startsWith('http://') || caminho.startsWith('https://')) {
            return caminho;
        }
        // Força o domínio correto da aplicação para carregar a imagem tanto no card quanto no preview
        return `https://app.universomidiadigital.com.br${caminho.startsWith('/') ? '' : '/'}${caminho}`;
    };

    // Função para selecionar o template e carregar no FORM e PREVIEW
    const selecionarTemplate = (t) => {
        setTemplateId(t.id);
        setNome(t.NomeTemplate || '');
        setDescricao(t.Descricao || '');
        setTipoArquivo(t.TipoArquivo || 'txt');
        setUrlVideo(t.UrlVideo || '');
        
        if (t.CaminhoImagem && t.TipoArquivo === 'imagem') {
            setPreviewUrl(getImageUrl(t.CaminhoImagem));
        } else {
            setPreviewUrl('');
        }
        setFile(null);
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const limparFormulario = () => {
        setTemplateId(null);
        setNome('');
        setDescricao('');
        setTipoArquivo('txt');
        setFile(null);
        setPreviewUrl('');
        setUrlVideo('');
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
        }
    };

    const handleTipoChange = (novoTipo) => {
        setTipoArquivo(novoTipo);
        if (novoTipo === 'txt') {
            setPreviewUrl('');
            setFile(null);
            setUrlVideo('');
        } else if (novoTipo === 'video') {
            setPreviewUrl('');
            setFile(null);
        } else if (novoTipo === 'imagem' && templateId) {
            const atual = templates.find(t => t.id === templateId);
            if (atual && atual.CaminhoImagem) {
                setPreviewUrl(getImageUrl(atual.CaminhoImagem));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('nome', nome);
        formData.append('descricao', descricao);
        formData.append('tipo', tipoArquivo);
        if (file) formData.append('file', file);
        if (urlVideo) formData.append('urlVideo', urlVideo);

        try {
            if (templateId) {
                await api.put(`/whatsapp/templates/${estabelecimentoId}/${templateId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success("Template atualizado com sucesso!");
            } else {
                await api.post(`/whatsapp/templates/${estabelecimentoId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success("Template criado com sucesso!");
            }
            limparFormulario();
            buscarTemplates();
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao processar template.");
        }
    };

    const handleDeletar = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Deseja realmente excluir este template?")) return;
        try {
            await api.delete(`/whatsapp/templates/${estabelecimentoId}/${id}`);
            toast.success("Excluído com sucesso!");
            if (templateId === id) limparFormulario();
            buscarTemplates();
        } catch (err) {
            toast.error("Erro ao excluir template.");
        }
    };

    return (
        <div className="template-manager-container">
            <div className="template-top-section">
                <div className="left-side">
                    <div className="header-flex">
                        <h3>{templateId ? `Editando: ${nome}` : 'Criar Novo Template'}</h3>
                        {templateId && (
                            <button className="cssbuttons-io-button" onClick={limparFormulario}>
                                <PlusCircle size={16} /> Novo Template
                            </button>
                        )}
                    </div>

                    <div className="vars-grid">
                        {variaveis.map(v => (
                            <div key={v} draggable onDragStart={(e) => e.dataTransfer.setData("text", v)} className="var-badge">
                                {v}
                            </div>
                        ))}
                    </div>

                    <form className="template-form" onSubmit={handleSubmit}>
                        <label>Nome do Template</label>
                        <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Boas-vindas" required />

                        <label>Descrição (Corpo da Mensagem)</label>
                        <textarea 
                            value={descricao} 
                            onChange={e => setDescricao(e.target.value)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDescricao(prev => prev + " " + e.dataTransfer.getData("text"));
                            }}
                            rows={5}
                            placeholder="Escreva sua mensagem aqui..."
                        />

                        <div className="type-selector">
                            <button type="button" onClick={() => handleTipoChange('txt')} className={tipoArquivo === 'txt' ? 'active' : ''}><Type size={16}/> Texto</button>
                            <button type="button" onClick={() => handleTipoChange('imagem')} className={tipoArquivo === 'imagem' ? 'active' : ''}><ImageIcon size={16}/> Imagem</button>
                            <button type="button" onClick={() => handleTipoChange('video')} className={tipoArquivo === 'video' ? 'active' : ''}><Video size={16}/> Vídeo</button>
                        </div>

                        {tipoArquivo === 'imagem' && (
                            <div className="file-input-group" style={{ marginTop: '10px' }}>
                                <label style={{ fontSize: '12px', color: '#64748b' }}>Selecione a Imagem (PNG, JPG, GIF):</label>
                                <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'block', marginTop: '5px' }} />
                                {templateId && !file && <small style={{ color: '#0284c7' }}>Deixe vazio para manter a imagem atual</small>}
                            </div>
                        )}
                        {tipoArquivo === 'video' && (
                            <div style={{ marginTop: '10px' }}>
                                <input placeholder="URL do Vídeo (mp4)" value={urlVideo} onChange={e => setUrlVideo(e.target.value)} style={{ width: '100%', padding: '8px' }} />
                            </div>
                        )}

                        <button type="submit" className="btn-save" style={{ marginTop: '15px' }}>
                            {templateId ? 'Atualizar Template' : 'Salvar Template'}
                        </button>
                    </form>
                </div>

                {/* Preview dinâmico */}
                <div className="preview-phone">
                    <div className="phone-header">Preview</div>
                    <div className="phone-body">
                        <div className="message-bubble">
                            {tipoArquivo === 'imagem' && previewUrl && (
                                <img src={previewUrl} alt="preview" style={{ width: '100%', borderRadius: '8px', marginBottom: '8px', objectFit: 'cover', maxHeight: '180px' }} />
                            )}
                            {tipoArquivo === 'video' && urlVideo && (
                                <video src={urlVideo} controls width="100%" key={urlVideo} style={{ borderRadius: '8px', marginBottom: '8px' }} />
                            )}
                            <p style={{ margin: '0 0 5px 0' }}><strong>{nome || 'Nome do Template'}</strong></p>
                            <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '13px' }}>{descricao || 'Sua mensagem aparecerá aqui...'}</p>
                            <span className="time" style={{ display: 'block', textAlign: 'right', fontSize: '10px', color: '#999', marginTop: '4px' }}>12:00</span>
                        </div>
                    </div>
                </div>
            </div>

            <hr className="divider" />

            <div className="template-list-section">
                <div className="list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3>Templates Cadastrados ({templates.length})</h3>
                    <button onClick={buscarTemplates} className="btn-refresh" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><RefreshCw size={16} /></button>
                </div>

                {loading ? (
                    <p>Carregando templates...</p>
                ) : (
                   <div className="templates-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '15px' }}>
                    {templates.map(t => (
                        <div 
                            key={t.id} 
                            className={`template-card-box ${templateId === t.id ? 'selected' : ''}`}
                            onClick={() => selecionarTemplate(t)}
                            style={{ border: templateId === t.id ? '2px solid #ea1d2c' : '1px solid #e2e8f0', padding: '12px', borderRadius: '10px', background: '#fff', cursor: 'pointer' }}
                        >
                            <div className="card-header-tag" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span className={`type-badge ${t.TipoArquivo}`} style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                    {t.TipoArquivo === 'imagem' ? '🖼️ Imagem' : t.TipoArquivo === 'video' ? '🎥 Vídeo' : '📝 Texto'}
                                </span>
                                <button onClick={(e) => handleDeletar(e, t.id)} className="btn-del-icon" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                                    <Trash2 size={16} />
                                </button>
                            </div>
                
                            <div className="card-body-content">
                                <strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>{t.NomeTemplate}</strong>
                                <p style={{ fontSize: '12px', color: '#64748b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                    {t.Descricao || "Sem descrição..."}
                                </p>
                            </div>
                
                            {t.CaminhoImagem && t.TipoArquivo === 'imagem' && (
                                <div className="card-footer-preview" style={{ marginTop: '10px' }}>
                                    <img src={getImageUrl(t.CaminhoImagem)} alt="thumb" style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '6px' }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                )}
            </div>
        </div>
    );
};

export default TemplateManager;